import { useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, FileUp } from "lucide-react";
import { ADDRESS_STATUSES, statusAllowed, FUTURE_STATUS_HINT } from "@/lib/constants";
import { FieldError } from "@/components/States";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { AddressRoleFields } from "@/components/AddressRoleFields";
import { hasMeterDetails, normalizeMeterDetails } from "@/components/MeterDetailsFields";
import { ImportAddressesDialog } from "./ImportAddressesDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

let keySeq = 0;
export const newAddressRow = (a = {}) => {
  const meters = normalizeMeterDetails(a.meter_details);
  return {
    key: `row-${++keySeq}`, id: a.id || null, postcode: a.postcode || "", full_address: a.full_address || "",
    meter_details: meters.length ? meters : [{ type: "", serial: "" }],
    instructions: a.instructions || "", notes: a.notes || "", agent_notes: a.agent_notes || "", engineer_notes: a.engineer_notes || "", dog_handler_notes: a.dog_handler_notes || "",
    status: a.status || "scheduled", orig_status: a.status || null,
    customer_name: a.customer_name || "", customer_telephone: a.customer_telephone || "", debt_amount: a.debt_amount || "",
    vulnerability_text: a.vulnerability_text || "", previous_visits: a.previous_visits ?? "", engineer_type: a.engineer_type || "", engineer_id: a.engineer_id || "", dog_handler_id: a.dog_handler_id || "",
    has_shutter: !!a.has_shutter, shutter_details: a.shutter_details || "",
  };
};

const AddressRow = ({ row, index, count, errors, update, move, onRemove, runDate, warrantTypes, engineers, dogHandlers, runEngineerId, jobTypes }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid={`address-row-${index}`}>
    <div className="mb-3 flex items-center justify-between">
      <span className="font-data text-xs font-semibold uppercase tracking-wider text-slate-500">Address {index + 1}</span>
      <div className="flex gap-1">
        <Button type="button" size="icon" variant="ghost" disabled={index === 0} onClick={() => move(index, -1)} aria-label="Move up" data-testid={`address-row-${index}-up`}><ArrowUp className="h-4 w-4" /></Button>
        <Button type="button" size="icon" variant="ghost" disabled={index === count - 1} onClick={() => move(index, 1)} aria-label="Move down" data-testid={`address-row-${index}-down`}><ArrowDown className="h-4 w-4" /></Button>
        <Button type="button" size="icon" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={onRemove} aria-label="Remove address" data-testid={`address-row-${index}-remove`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
    <div className="grid gap-3 md:grid-cols-[150px_1fr_170px]">
      <div>
        <Label htmlFor={`pc-${row.key}`}>Postcode *</Label>
        <Input id={`pc-${row.key}`} value={row.postcode} onChange={(e) => update(index, { postcode: e.target.value })} className="font-data mt-1 uppercase" data-testid={`address-row-${index}-postcode`} />
        <FieldError message={errors?.postcode} testId={`address-row-${index}-postcode-error`} />
      </div>
      <div>
        <Label htmlFor={`fa-${row.key}`}>Full address <span className="font-normal text-slate-500">(optional)</span></Label>
        <Input id={`fa-${row.key}`} value={row.full_address} onChange={(e) => update(index, { full_address: e.target.value })} className="mt-1" data-testid={`address-row-${index}-full-address`} />
        <FieldError message={errors?.full_address} testId={`address-row-${index}-address-error`} />
      </div>
      <div>
        <Label>Status</Label>
        <Select value={row.status} onValueChange={(v) => update(index, { status: v })}>
          <SelectTrigger className="mt-1" data-testid={`address-row-${index}-status`}><SelectValue /></SelectTrigger>
          <SelectContent>{ADDRESS_STATUSES.map((s) => <SelectItem key={s.value} value={s.value} disabled={!statusAllowed(runDate, s.value)}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
        <FieldError message={errors?.status && FUTURE_STATUS_HINT} testId={`address-row-${index}-status-error`} />
      </div>
    </div>
    <div className="mt-3">
      <AddressRoleFields value={row} onChange={(p) => update(index, p)} warrantTypes={warrantTypes} testId={`address-row-${index}`} engineers={engineers} dogHandlers={dogHandlers} runEngineerId={runEngineerId} allowEngineerAssignment jobTypes={jobTypes} meterDetailsError={errors?.meter_details} />
    </div>
    <div className="mt-3">
      <div>
        <Label htmlFor={`in-${row.key}`}>Manager notes</Label>
        <Textarea id={`in-${row.key}`} rows={2} value={row.instructions} onChange={(e) => update(index, { instructions: e.target.value })} className="mt-1" data-testid={`address-row-${index}-instructions`} />
      </div>
    </div>
  </div>
);

export const AddressRowsEditor = ({ rows, setRows, errors, listError, runDate, warrantTypes, engineers, dogHandlers, runEngineerId, jobTypes }) => {
  const [pending, setPending] = useState(null);
  const [importing, setImporting] = useState(false);
  const isBlank = (r) => !r.id && !r.has_shutter && ![r.postcode, r.full_address, r.instructions, r.notes, r.agent_notes, r.engineer_notes, r.dog_handler_notes, r.shutter_details, r.customer_name, r.customer_telephone, r.debt_amount, r.vulnerability_text, r.engineer_type, r.engineer_id, r.dog_handler_id].some((value) => String(value).trim()) && !hasMeterDetails(r.meter_details) && !String(r.previous_visits).trim();
  const addImported = (list) => setRows([...rows.filter((r) => !isBlank(r)), ...list.map(newAddressRow)]);
  const update = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const move = (i, d) => { const next = [...rows]; [next[i], next[i + d]] = [next[i + d], next[i]]; setRows(next); };
  const removeAt = (i) => setRows(rows.filter((_, j) => j !== i));

  return (
    <div className="space-y-3">
      {rows.map((row, i) => (
        <AddressRow key={row.key} row={row} index={i} count={rows.length} errors={errors[i]} update={update} move={move} runDate={runDate} warrantTypes={warrantTypes} engineers={engineers} dogHandlers={dogHandlers} runEngineerId={runEngineerId} jobTypes={jobTypes}
          onRemove={() => (row.id ? setPending(i) : removeAt(i))} />
      ))}
      <FieldError message={listError} testId="addresses-list-error" />
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={() => setRows([...rows, newAddressRow()])} data-testid="add-address-row-button">
          <Plus className="mr-1 h-4 w-4" /> Add address
        </Button>
        <Button type="button" variant="outline" onClick={() => setImporting(true)} data-testid="import-addresses-button">
          <FileUp className="mr-1 h-4 w-4" /> Import addresses
        </Button>
      </div>
      <ImportAddressesDialog open={importing} onOpenChange={setImporting} onImport={addImported} />
      <ConfirmDialog open={pending !== null} onOpenChange={(o) => !o && setPending(null)} title="Remove this address?"
        description="The address and its notes will be permanently deleted when you save the run." confirmLabel="Remove address"
        onConfirm={() => { removeAt(pending); setPending(null); }} />
    </div>
  );
};
